import React, { useEffect, useState } from 'react';
import {
  Modal,
  Group,
  Button,
  Stack,
  Text,
  Table,
  Checkbox,
  Title,
  LoadingOverlay,
  Radio,
  Avatar,
  Tabs,
  TextInput
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { Participant } from 'pages/app/LIA/AssessmentSchedules/utils/schema';
import { dataBinusService } from 'services/dataBinusService';
import useDebounce from 'hooks/useDebounce';
import { IconSearch } from '@tabler/icons-react';

/* eslint-disable */
interface RespondentSelectionModalProps {
  opened: boolean;
  onClose: () => void;
  participant: Participant;
  existingRespondents: Participant[];
  onSubmit: (selectedRespondents: Participant[]) => void;
}
/* eslint-enable */

interface RespondentFilterOptions {
  isPeer: boolean;
  isSupervisor: boolean;
  isSubordinate: boolean;
}

const RespondentSelectionModal: React.FC<RespondentSelectionModalProps> = ({
  opened,
  onClose,
  participant,
  existingRespondents,
  onSubmit
}) => {
  const [selectedRespondents, setSelectedRespondents] = useState<Participant[]>([]);
  const [respondentFilters, setRespondentFilters] = useState<RespondentFilterOptions>({
    isPeer: true,
    isSupervisor: true,
    isSubordinate: true
  });
  const [activeTab, setActiveTab] = useState<string>('internal');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  useEffect(() => {
    if (opened) {
      setSelectedRespondents([]);
      setRespondentFilters({
        isPeer: true,
        isSupervisor: true,
        isSubordinate: true
      });
      setActiveTab('internal');
      setSearchQuery('');
    }
  }, [opened]);

  // Get organization-related respondents for the selected participant
  const { data: internalRespondents, isLoading: isLoadingInternalRespondents } = useQuery({
    queryKey: ['respondents', participant?.email || participant?.binusianId, respondentFilters],
    queryFn: async () => {
      const response = await dataBinusService.getRespondentList({
        email: participant?.email || participant?.binusianId || '',
        ispeer: respondentFilters.isPeer,
        isspv: respondentFilters.isSupervisor,
        issubordinate: respondentFilters.isSubordinate
      });
      // Filter out duplicates based on email, keeping one random respondent per email
      if (response.data) {
        const emailMap = new Map<string, any>();
        response.data.forEach((respondent: any) => {
          if (respondent.email && !emailMap.has(respondent.email)) {
            emailMap.set(respondent.email, respondent);
          }
        });
        response.data = Array.from(emailMap.values());
      }
      return response;
    },
    enabled: opened && !!participant && activeTab === 'internal'
  });

  // Get external respondents based on search
  const { data: externalRespondents, isLoading: isLoadingExternalRespondents } = useQuery({
    queryKey: ['emplecList', debouncedSearchQuery],
    queryFn: async () => {
      const response = await dataBinusService.getEmplecList({
        search: debouncedSearchQuery,
        isEmployee: true,
        isLecturer: true
      });
      // Filter out duplicates in employees and lecturers based on email
      if (response.employees?.data) {
        const emailMap = new Map<string, any>();
        response.employees.data.forEach((emp: any) => {
          if (emp.email && !emailMap.has(emp.email)) {
            emailMap.set(emp.email, emp);
          }
        });
        response.employees.data = Array.from(emailMap.values());
      }
      if (response.lecturers?.data) {
        const emailMap = new Map<string, any>();
        response.lecturers.data.forEach((lec: any) => {
          if (lec.email && !emailMap.has(lec.email)) {
            emailMap.set(lec.email, lec);
          }
        });
        response.lecturers.data = Array.from(emailMap.values());
      }
      return response;
    },
    enabled: opened && activeTab === 'external' && debouncedSearchQuery.length > 2
  });

  // Function to check if a respondent is already in existingRespondents list
  const isRespondentExisting = (respondent: Participant) => {
    return existingRespondents.some(
      (r) =>
        r.binusianId === respondent.binusianId &&
        r.participants?.some((p) => p.binusianId === participant.binusianId)
    );
  };

  const handleToggleRespondent = (respondent: Participant, isExternal: boolean) => {
    setSelectedRespondents((prev) => {
      const isSelected = prev.some((r) => r.binusianId === respondent.binusianId);

      if (isSelected) {
        return prev.filter((r) => r.binusianId !== respondent.binusianId);
      } else {
        // Add the participantId and isExternalStructural flag
        return [
          ...prev,
          {
            ...respondent,
            participantId: participant.binusianId,
            participants: [
              {
                binusianId: participant.binusianId,
                isExternalStructural: isExternal
              }
            ]
          }
        ];
      }
    });
  };

  const handleSubmit = () => {
    onSubmit(selectedRespondents);
    onClose();
  };

  const renderInternalRespondents = () => {
    if (isLoadingInternalRespondents) {
      return <LoadingOverlay visible={true} />;
    }

    if (!internalRespondents?.data || internalRespondents.data.length === 0) {
      return (
        <Text c='dimmed' ta='center' mt='md'>
          No respondents found for this participant
        </Text>
      );
    }

    return (
      <Table striped border={1}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>Division</Table.Th>
            <Table.Th>Type</Table.Th>
            <Table.Th style={{ width: 80 }}>Select</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {internalRespondents.data.map((respondent: any) => {
            const mappedRespondent: Participant = {
              binusianId: respondent.binusianId,
              name: respondent.nama || 'Unknown',
              division: respondent.division || 'Unknown',
              type: respondent.respondentType || 'Unknown',
              email: respondent.email
            };

            const isExisting = isRespondentExisting(mappedRespondent);

            return (
              <Table.Tr key={respondent.binusianId} opacity={isExisting ? 0.5 : 1}>
                <Table.Td>
                  <Group>
                    <Avatar color='#7B2183' radius='xl'>
                      {mappedRespondent.name && mappedRespondent.name.length > 0
                        ? mappedRespondent.name.charAt(0)
                        : '?'}
                    </Avatar>
                    <div>
                      <Text size='sm' fw={500}>
                        {mappedRespondent.name}
                      </Text>
                      <Text size='xs' c='dimmed'>
                        {mappedRespondent.binusianId}
                      </Text>
                      {isExisting && (
                        <Text size='xs' c='red'>
                          Already added
                        </Text>
                      )}
                    </div>
                  </Group>
                </Table.Td>
                <Table.Td>{mappedRespondent.division}</Table.Td>
                <Table.Td>{mappedRespondent.type}</Table.Td>
                <Table.Td>
                  <Checkbox
                    disabled={isExisting}
                    checked={
                      isExisting ||
                      selectedRespondents.some((r) => r.binusianId === mappedRespondent.binusianId)
                    }
                    onChange={() => !isExisting && handleToggleRespondent(mappedRespondent, false)}
                  />
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    );
  };

  const renderExternalRespondents = () => {
    if (isLoadingExternalRespondents) {
      return <LoadingOverlay visible={true} />;
    }

    if (!debouncedSearchQuery || debouncedSearchQuery.length <= 2) {
      return (
        <Text c='dimmed' ta='center' mt='md'>
          Enter at least 3 characters to search for external respondents
        </Text>
      );
    }

    const employeeRespondents = externalRespondents?.employees?.data || [];
    const lecturerRespondents = externalRespondents?.lecturers?.data || [];
    const allExternalRespondents = [...employeeRespondents, ...lecturerRespondents];

    // Filter out duplicates based on email
    const uniqueExternalRespondents = allExternalRespondents.filter(
      (respondent, index, self) => self.findIndex((r) => r.email === respondent.email) === index
    );

    if (uniqueExternalRespondents.length === 0) {
      return (
        <Text c='dimmed' ta='center' mt='md'>
          No external respondents found matching your search
        </Text>
      );
    }

    return (
      <Table striped border={1}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>Email</Table.Th>
            <Table.Th>Type</Table.Th>
            <Table.Th style={{ width: 80 }}>Select</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {uniqueExternalRespondents.map((respondent: any) => {
            const mappedRespondent: Participant = {
              binusianId: respondent.binusianId,
              name: respondent.name || 'Unknown',
              division: respondent.bu || respondent.fakultas || 'Unknown',
              type: respondent.bu ? 'Employee' : 'Lecturer',
              email: respondent.email
            };

            const isExisting = isRespondentExisting(mappedRespondent);

            return (
              <Table.Tr key={respondent.binusianId} opacity={isExisting ? 0.5 : 1}>
                <Table.Td>
                  <Group>
                    <Avatar color='#7B2183' radius='xl'>
                      {mappedRespondent.name && mappedRespondent.name.length > 0
                        ? mappedRespondent.name.charAt(0)
                        : '?'}
                    </Avatar>
                    <div>
                      <Text size='sm' fw={500}>
                        {mappedRespondent.name}
                      </Text>
                      <Text size='xs' c='dimmed'>
                        {mappedRespondent.binusianId}
                      </Text>
                      {isExisting && (
                        <Text size='xs' c='red'>
                          Already added
                        </Text>
                      )}
                    </div>
                  </Group>
                </Table.Td>
                <Table.Td>{mappedRespondent.email || 'N/A'}</Table.Td>
                <Table.Td>{mappedRespondent.type}</Table.Td>
                <Table.Td>
                  <Checkbox
                    disabled={isExisting}
                    checked={
                      isExisting ||
                      selectedRespondents.some((r) => r.binusianId === mappedRespondent.binusianId)
                    }
                    onChange={() => !isExisting && handleToggleRespondent(mappedRespondent, true)}
                  />
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    );
  };

  return (
    <Modal opened={opened} centered onClose={onClose} size='xl' withCloseButton={false}>
      <Stack gap='md' p={16}>
        <Title order={3}>Select Respondents for {participant?.name || 'Participant'}</Title>

        <Tabs
          defaultValue='internal'
          value={activeTab}
          onChange={(value) => setActiveTab(value || 'internal')}
        >
          <Tabs.List>
            <Tabs.Tab value='internal'>Organization Respondents</Tabs.Tab>
            <Tabs.Tab value='external'>External Respondents</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value='internal' pt='md'>
            <Group mb='md'>
              <Text fw={500}>Filter Respondents:</Text>
              <Radio.Group defaultValue='all'>
                <Group>
                  <Radio
                    value='peer'
                    label='Peer'
                    checked={
                      respondentFilters.isPeer &&
                      !respondentFilters.isSupervisor &&
                      !respondentFilters.isSubordinate
                    }
                    onChange={() =>
                      setRespondentFilters({
                        isPeer: true,
                        isSupervisor: false,
                        isSubordinate: false
                      })
                    }
                  />
                  <Radio
                    value='supervisor'
                    label='Supervisor'
                    checked={
                      !respondentFilters.isPeer &&
                      respondentFilters.isSupervisor &&
                      !respondentFilters.isSubordinate
                    }
                    onChange={() =>
                      setRespondentFilters({
                        isPeer: false,
                        isSupervisor: true,
                        isSubordinate: false
                      })
                    }
                  />
                  <Radio
                    value='subordinate'
                    label='Subordinate'
                    checked={
                      !respondentFilters.isPeer &&
                      !respondentFilters.isSupervisor &&
                      respondentFilters.isSubordinate
                    }
                    onChange={() =>
                      setRespondentFilters({
                        isPeer: false,
                        isSupervisor: false,
                        isSubordinate: true
                      })
                    }
                  />
                  <Radio
                    value='all'
                    label='All Types'
                    checked={
                      respondentFilters.isPeer &&
                      respondentFilters.isSupervisor &&
                      respondentFilters.isSubordinate
                    }
                    onChange={() =>
                      setRespondentFilters({
                        isPeer: true,
                        isSupervisor: true,
                        isSubordinate: true
                      })
                    }
                  />
                </Group>
              </Radio.Group>
            </Group>

            <div style={{ position: 'relative', minHeight: '300px' }}>
              {renderInternalRespondents()}
            </div>
          </Tabs.Panel>

          <Tabs.Panel value='external' pt='md'>
            <TextInput
              label='Search External Respondents'
              placeholder='Search by name or ID...'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.currentTarget.value)}
              rightSection={<IconSearch size={16} />}
              mb='md'
            />

            <div style={{ position: 'relative', minHeight: '300px' }}>
              {renderExternalRespondents()}
            </div>
          </Tabs.Panel>
        </Tabs>

        <Group justify='flex-end' mt='md'>
          <Button variant='outline' onClick={onClose} color='#7B2183'>
            Cancel
          </Button>
          <Button onClick={handleSubmit} color='#7B2183'>
            Add {selectedRespondents.length} Respondent{selectedRespondents.length !== 1 ? 's' : ''}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default RespondentSelectionModal;
